import { useMemo, useState } from 'react'
import {
  Box, Breadcrumbs, Typography, Stack, Tabs, Tab, Autocomplete, TextField, Button, Paper,
  Table, TableHead, TableRow, TableCell, TableBody, Chip,
} from '@mui/material'
import { Download } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { listArticles } from '../api/nomenclatoare'
import { listWarehouses } from '../api/depozite'
import {
  getStockValuation, getStockValuationExportUrl, type StockValuation,
  listInventoryCounts, getInventoryCountsExportUrl, type InventoryCountListItem,
} from '../api/stocuri'
import {
  listInvoices, getInvoicesExportUrl, getAgingReport, getAgingReportExportUrl,
  getSalesJournal, getSalesJournalExportUrl,
  type InvoiceListItem, type AgingBucket,
} from '../api/financiar'
import {
  listSupplierInvoices, getSupplierInvoicesExportUrl, getSupplierAgingReport, getSupplierAgingReportExportUrl,
  getPurchaseJournal, getPurchaseJournalExportUrl,
  type SupplierInvoiceListItem, type SupplierAgingBucket,
} from '../api/facturiFurnizor'
import { listClients } from '../api/clienti'
import { listSuppliers } from '../api/furnizori'
import { listUsers } from '../api/admin'
import { getOrdersInProgress, getOrdersInProgressExportUrl, type OrderInProgress } from '../api/comenzi'
import {
  getProductionOrdersStatusReport, getProductionOrdersStatusReportExportUrl,
  type ProductionOrderStatusReportRow, type ProductionOrderReportBucket,
} from '../api/productieOrdine'

function downloadBlob(blob: Blob) {
  window.open(URL.createObjectURL(blob), '_blank')
}

function firstDayOfMonth(): string {
  const d = new Date()
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10)
}
function today(): string {
  return new Date().toISOString().slice(0, 10)
}

export default function RapoarteCatalogPage() {
  const [tab, setTab] = useState(0)
  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Rapoarte</Typography>
        <Typography variant="inherit" color="text.primary">Catalog rapoarte</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Catalog rapoarte</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable">
        <Tab label="Stoc curent" />
        <Tab label="Facturi emise" />
        <Tab label="Facturi primite" />
        <Tab label="Creanțe clienți" />
        <Tab label="Datorii furnizori" />
        <Tab label="Jurnal vânzări" />
        <Tab label="Jurnal achiziții" />
        <Tab label="Registru inventar" />
        <Tab label="Comenzi client în curs" />
        <Tab label="Ordine de producție" />
      </Tabs>
      {tab === 0 && <StocCurentTab />}
      {tab === 1 && <FacturiEmiseTab />}
      {tab === 2 && <FacturiPrimiteTab />}
      {tab === 3 && <CreanteClientiTab />}
      {tab === 4 && <DatoriiFurnizoriTab />}
      {tab === 5 && <JurnalVanzariTab />}
      {tab === 6 && <JurnalAchizitiiTab />}
      {tab === 7 && <RegistruInventarTab />}
      {tab === 8 && <ComenziInCursTab />}
      {tab === 9 && <OrdineProductieRaportTab />}
    </Stack>
  )
}

function ExportButton({ onExport, pending }: { onExport: () => void; pending: boolean }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Button variant="outlined" startIcon={<Download />} disabled={pending} onClick={onExport}>Export CSV</Button>
    </Box>
  )
}

// ---------- Stoc curent (§12.2 #4) ----------

function StocCurentTab() {
  const navigate = useNavigate()
  const [articleId, setArticleId] = useState<string | null>(null)
  const [warehouseId, setWarehouseId] = useState<string | null>(null)
  const { data: articles } = useQuery({ queryKey: ['nomenclatoare', 'articole', 'toate'], queryFn: () => listArticles() })
  const { data: warehouses } = useQuery({ queryKey: ['stocuri', 'depozite'], queryFn: listWarehouses })
  const { data, isLoading } = useQuery({
    queryKey: ['rapoarte', 'catalog', 'stoc', articleId, warehouseId],
    queryFn: () => getStockValuation({ articleId: articleId ?? undefined, warehouseId: warehouseId ?? undefined }),
  })
  const exportMutation = useMutation({
    mutationFn: () => getStockValuationExportUrl({ articleId: articleId ?? undefined, warehouseId: warehouseId ?? undefined }),
    onSuccess: downloadBlob,
  })

  const columns = useMemo<GridColDef<StockValuation>[]>(() => [
    { field: 'articleCode', headerName: 'Cod', width: 120 },
    { field: 'articleDescription', headerName: 'Articol', flex: 1, minWidth: 180 },
    { field: 'warehouseName', headerName: 'Depozit', width: 160 },
    { field: 'quantity', headerName: 'Cantitate', width: 110, type: 'number', valueFormatter: (v: number) => v.toFixed(3).replace('.', ',') },
    { field: 'standardCost', headerName: 'Cost standard', width: 130, type: 'number', valueFormatter: (v: number | null) => v?.toFixed(2).replace('.', ',') ?? '—' },
    { field: 'value', headerName: 'Valoare', width: 130, type: 'number', valueFormatter: (v: number) => v.toFixed(2).replace('.', ',') },
  ], [])

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2}>
        <Autocomplete
          sx={{ width: 320 }} options={articles?.map((a) => a.id) ?? []}
          getOptionLabel={(id) => { const a = articles?.find((x) => x.id === id); return a ? `${a.code} — ${a.shortDescription}` : id }}
          value={articleId} onChange={(_, v) => setArticleId(v)}
          renderInput={(params) => <TextField {...params} label="Filtru articol" />}
        />
        <Autocomplete
          sx={{ width: 240 }} options={warehouses?.map((w) => w.id) ?? []}
          getOptionLabel={(id) => warehouses?.find((w) => w.id === id)?.name ?? id}
          value={warehouseId} onChange={(_, v) => setWarehouseId(v)}
          renderInput={(params) => <TextField {...params} label="Filtru depozit" />}
        />
      </Stack>
      <ExportButton onExport={() => exportMutation.mutate()} pending={exportMutation.isPending} />
      <Box sx={{ height: 520, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} getRowId={(r) => `${r.articleId}-${r.warehouseId}`} loading={isLoading}
          density="comfortable" disableRowSelectionOnClick onRowClick={() => navigate('/stocuri/stoc')}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>
    </Stack>
  )
}

// ---------- Facturi emise (§12.2 #6) ----------

function FacturiEmiseTab() {
  const navigate = useNavigate()
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [clientId, setClientId] = useState<string | null>(null)
  const [agentUserId, setAgentUserId] = useState<string | null>(null)
  const { data: clients } = useQuery({ queryKey: ['comercial', 'clienti', 'toate'], queryFn: () => listClients() })
  const { data: users } = useQuery({ queryKey: ['administrare', 'utilizatori', 'toate'], queryFn: () => listUsers() })
  const filters = { from: from || undefined, to: to || undefined, clientId: clientId ?? undefined, agentUserId: agentUserId ?? undefined }
  const { data, isLoading } = useQuery({
    queryKey: ['rapoarte', 'catalog', 'facturi-emise', from, to, clientId, agentUserId],
    queryFn: () => listInvoices(filters),
  })
  const exportMutation = useMutation({ mutationFn: () => getInvoicesExportUrl(filters), onSuccess: downloadBlob })

  const columns = useMemo<GridColDef<InvoiceListItem>[]>(() => [
    { field: 'number', headerName: 'Număr', width: 150 },
    { field: 'issueDate', headerName: 'Dată emitere', width: 120 },
    { field: 'clientName', headerName: 'Client', flex: 1, minWidth: 180 },
    { field: 'total', headerName: 'Total', width: 120, type: 'number', valueFormatter: (v: number) => v.toFixed(2).replace('.', ',') },
    { field: 'currency', headerName: 'Monedă', width: 90 },
    { field: 'status', headerName: 'Status', width: 110 },
    { field: 'overdueDays', headerName: 'Zile restante', width: 120, valueGetter: (v: number | null) => v ?? '' },
  ], [])

  return (
    <Stack spacing={2}>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 2 }}>
        <TextField label="De la" type="date" size="small" value={from} onChange={(e) => setFrom(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
        <TextField label="Până la" type="date" size="small" value={to} onChange={(e) => setTo(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
        <Autocomplete
          sx={{ width: 260 }} options={clients?.map((c) => c.id) ?? []}
          getOptionLabel={(id) => clients?.find((c) => c.id === id)?.name ?? id}
          value={clientId} onChange={(_, v) => setClientId(v)}
          renderInput={(params) => <TextField {...params} label="Client" />}
        />
        <Autocomplete
          sx={{ width: 220 }} options={users?.map((u) => u.id) ?? []}
          getOptionLabel={(id) => { const u = users?.find((x) => x.id === id); return u ? `${u.firstName} ${u.lastName}` : id }}
          value={agentUserId} onChange={(_, v) => setAgentUserId(v)}
          renderInput={(params) => <TextField {...params} label="Agent" />}
        />
      </Stack>
      <ExportButton onExport={() => exportMutation.mutate()} pending={exportMutation.isPending} />
      <Box sx={{ height: 520, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          onRowClick={() => navigate('/financiar/facturi')}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>
    </Stack>
  )
}

// ---------- Facturi primite (§12.2 #7) ----------

function FacturiPrimiteTab() {
  const navigate = useNavigate()
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [supplierId, setSupplierId] = useState<string | null>(null)
  const { data: suppliers } = useQuery({ queryKey: ['achizitii', 'furnizori', 'toate'], queryFn: () => listSuppliers() })
  const filters = { from: from || undefined, to: to || undefined, supplierId: supplierId ?? undefined }
  const { data, isLoading } = useQuery({
    queryKey: ['rapoarte', 'catalog', 'facturi-primite', from, to, supplierId],
    queryFn: () => listSupplierInvoices(filters),
  })
  const exportMutation = useMutation({ mutationFn: () => getSupplierInvoicesExportUrl(filters), onSuccess: downloadBlob })

  const columns = useMemo<GridColDef<SupplierInvoiceListItem>[]>(() => [
    { field: 'number', headerName: 'Număr', width: 150 },
    { field: 'issueDate', headerName: 'Dată emitere', width: 120 },
    { field: 'supplierName', headerName: 'Furnizor', flex: 1, minWidth: 180 },
    { field: 'total', headerName: 'Total', width: 120, type: 'number', valueFormatter: (v: number) => v.toFixed(2).replace('.', ',') },
    { field: 'currency', headerName: 'Monedă', width: 90 },
    { field: 'status', headerName: 'Status', width: 140 },
    {
      field: 'hasMatchingException', headerName: 'Excepție', width: 100,
      renderCell: (p) => (p.row.hasMatchingException ? <Chip size="small" label="Da" color="warning" variant="outlined" /> : null),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 2 }}>
        <TextField label="De la" type="date" size="small" value={from} onChange={(e) => setFrom(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
        <TextField label="Până la" type="date" size="small" value={to} onChange={(e) => setTo(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
        <Autocomplete
          sx={{ width: 260 }} options={suppliers?.map((s) => s.id) ?? []}
          getOptionLabel={(id) => suppliers?.find((s) => s.id === id)?.name ?? id}
          value={supplierId} onChange={(_, v) => setSupplierId(v)}
          renderInput={(params) => <TextField {...params} label="Furnizor" />}
        />
      </Stack>
      <ExportButton onExport={() => exportMutation.mutate()} pending={exportMutation.isPending} />
      <Box sx={{ height: 520, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          onRowClick={() => navigate('/achizitii/facturi')}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>
    </Stack>
  )
}

// ---------- Creanțe clienți / Datorii furnizori — aging pe bucket-uri (§12.2 #8/#9) ----------

function CreanteClientiTab() {
  const navigate = useNavigate()
  const { data, isLoading } = useQuery({ queryKey: ['rapoarte', 'catalog', 'creante-clienti'], queryFn: getAgingReport })
  const exportMutation = useMutation({ mutationFn: getAgingReportExportUrl, onSuccess: downloadBlob })
  return (
    <AgingBuckets
      buckets={data?.buckets} isLoading={isLoading} entityLabel="Client"
      getEntityName={(i: InvoiceListItem) => i.clientName} getNumber={(i: InvoiceListItem) => i.number}
      getDueDate={(i: InvoiceListItem) => i.dueDate} getTotal={(i: InvoiceListItem) => i.total} getCurrency={(i: InvoiceListItem) => i.currency}
      onExport={() => exportMutation.mutate()} exportPending={exportMutation.isPending}
      onRowClick={() => navigate('/financiar/facturi')}
    />
  )
}

function DatoriiFurnizoriTab() {
  const navigate = useNavigate()
  const { data, isLoading } = useQuery({ queryKey: ['rapoarte', 'catalog', 'datorii-furnizori'], queryFn: getSupplierAgingReport })
  const exportMutation = useMutation({ mutationFn: getSupplierAgingReportExportUrl, onSuccess: downloadBlob })
  return (
    <AgingBuckets
      buckets={data?.buckets} isLoading={isLoading} entityLabel="Furnizor"
      getEntityName={(i: SupplierInvoiceListItem) => i.supplierName} getNumber={(i: SupplierInvoiceListItem) => i.number}
      getDueDate={(i: SupplierInvoiceListItem) => i.dueDate} getTotal={(i: SupplierInvoiceListItem) => i.total} getCurrency={(i: SupplierInvoiceListItem) => i.currency}
      onExport={() => exportMutation.mutate()} exportPending={exportMutation.isPending}
      onRowClick={() => navigate('/achizitii/facturi')}
    />
  )
}

function AgingBuckets<T>({
  buckets, isLoading, entityLabel, getEntityName, getNumber, getDueDate, getTotal, getCurrency, onExport, exportPending, onRowClick,
}: {
  buckets: (AgingBucket | SupplierAgingBucket)[] | undefined
  isLoading: boolean
  entityLabel: string
  getEntityName: (i: T) => string
  getNumber: (i: T) => string
  getDueDate: (i: T) => string
  getTotal: (i: T) => number
  getCurrency: (i: T) => string
  onExport: () => void
  exportPending: boolean
  onRowClick: () => void
}) {
  return (
    <Stack spacing={2}>
      <ExportButton onExport={onExport} pending={exportPending} />
      {!isLoading && buckets && buckets.every((b) => b.invoices.length === 0) && (
        <Box sx={{ color: 'text.secondary' }}>Nicio factură restantă.</Box>
      )}
      {buckets?.filter((b) => b.invoices.length > 0).map((b) => (
        <Paper key={b.label} variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center' }}>
            <Chip size="small" label={b.label + ' zile'} color={b.label === '90+' ? 'error' : 'warning'} variant="outlined" />
            <Typography variant="subtitle2">{b.invoices.length} factură/i — total {b.total.toFixed(2).replace('.', ',')}</Typography>
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Număr</TableCell><TableCell>Scadență</TableCell><TableCell>{entityLabel}</TableCell>
                <TableCell align="right">Total</TableCell><TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {(b.invoices as T[]).map((i, idx) => (
                <TableRow key={idx} hover sx={{ cursor: 'pointer' }} onClick={onRowClick}>
                  <TableCell>{getNumber(i)}</TableCell>
                  <TableCell>{getDueDate(i)}</TableCell>
                  <TableCell>{getEntityName(i)}</TableCell>
                  <TableCell align="right">{getTotal(i).toFixed(2).replace('.', ',')} {getCurrency(i)}</TableCell>
                  <TableCell align="right" />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      ))}
    </Stack>
  )
}

// ---------- Jurnal vânzări / Jurnal achiziții — registre TVA (§12.2, obligatorii legal) ----------

interface JournalRow { invoiceNumber: string; issueDate: string; partnerName: string; vatRatePercent: number; netAmount: number; vatAmount: number; totalAmount: number }
interface JournalRateSummary { vatRatePercent: number; netAmount: number; vatAmount: number; totalAmount: number }

function JournalTable({
  partnerLabel, rows, summary, isLoading, from, to, onFromChange, onToChange, onExport, exportPending, onRowClick,
}: {
  partnerLabel: string
  rows: JournalRow[]
  summary: JournalRateSummary[]
  isLoading: boolean
  from: string
  to: string
  onFromChange: (v: string) => void
  onToChange: (v: string) => void
  onExport: () => void
  exportPending: boolean
  onRowClick: () => void
}) {
  const columns = useMemo<GridColDef<JournalRow>[]>(() => [
    { field: 'invoiceNumber', headerName: 'Număr', width: 140 },
    { field: 'issueDate', headerName: 'Dată', width: 110 },
    { field: 'partnerName', headerName: partnerLabel, flex: 1, minWidth: 180 },
    { field: 'vatRatePercent', headerName: 'Cotă TVA', width: 100, type: 'number', valueFormatter: (v: number) => `${v}%` },
    { field: 'netAmount', headerName: 'Bază', width: 120, type: 'number', valueFormatter: (v: number) => v.toFixed(2).replace('.', ',') },
    { field: 'vatAmount', headerName: 'TVA', width: 120, type: 'number', valueFormatter: (v: number) => v.toFixed(2).replace('.', ',') },
    { field: 'totalAmount', headerName: 'Total', width: 120, type: 'number', valueFormatter: (v: number) => v.toFixed(2).replace('.', ',') },
  ], [partnerLabel])

  return (
    <Stack spacing={2}>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 2 }}>
        <TextField label="De la" type="date" size="small" value={from} onChange={(e) => onFromChange(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
        <TextField label="Până la" type="date" size="small" value={to} onChange={(e) => onToChange(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
      </Stack>
      {from > to && <Box sx={{ color: 'error.main' }}>Data de final trebuie să fie după data de start.</Box>}
      {summary.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Sumar pe cotă TVA</Typography>
          <Table size="small">
            <TableHead>
              <TableRow><TableCell>Cotă</TableCell><TableCell align="right">Bază</TableCell><TableCell align="right">TVA</TableCell><TableCell align="right">Total</TableCell></TableRow>
            </TableHead>
            <TableBody>
              {summary.map((s) => (
                <TableRow key={s.vatRatePercent}>
                  <TableCell>{s.vatRatePercent}%</TableCell>
                  <TableCell align="right">{s.netAmount.toFixed(2).replace('.', ',')}</TableCell>
                  <TableCell align="right">{s.vatAmount.toFixed(2).replace('.', ',')}</TableCell>
                  <TableCell align="right">{s.totalAmount.toFixed(2).replace('.', ',')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
      <ExportButton onExport={onExport} pending={exportPending} />
      <Box sx={{ height: 480, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={rows} columns={columns} getRowId={(r) => `${r.invoiceNumber}-${r.vatRatePercent}`} loading={isLoading}
          density="comfortable" disableRowSelectionOnClick onRowClick={onRowClick}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>
    </Stack>
  )
}

function JurnalVanzariTab() {
  const navigate = useNavigate()
  const [from, setFrom] = useState(firstDayOfMonth())
  const [to, setTo] = useState(today())
  const { data, isLoading } = useQuery({
    queryKey: ['rapoarte', 'catalog', 'jurnal-vanzari', from, to],
    queryFn: () => getSalesJournal(from, to), enabled: from <= to,
  })
  const exportMutation = useMutation({ mutationFn: () => getSalesJournalExportUrl(from, to), onSuccess: downloadBlob })

  const rows = (data?.entries ?? []).map((e) => ({ ...e, partnerName: e.clientName }))
  return (
    <JournalTable partnerLabel="Client" rows={rows} summary={data?.summary ?? []} isLoading={isLoading}
      from={from} to={to} onFromChange={setFrom} onToChange={setTo}
      onExport={() => exportMutation.mutate()} exportPending={exportMutation.isPending}
      onRowClick={() => navigate('/financiar/facturi')} />
  )
}

function JurnalAchizitiiTab() {
  const navigate = useNavigate()
  const [from, setFrom] = useState(firstDayOfMonth())
  const [to, setTo] = useState(today())
  const { data, isLoading } = useQuery({
    queryKey: ['rapoarte', 'catalog', 'jurnal-achizitii', from, to],
    queryFn: () => getPurchaseJournal(from, to), enabled: from <= to,
  })
  const exportMutation = useMutation({ mutationFn: () => getPurchaseJournalExportUrl(from, to), onSuccess: downloadBlob })

  const rows = (data?.entries ?? []).map((e) => ({ ...e, partnerName: e.supplierName }))
  return (
    <JournalTable partnerLabel="Furnizor" rows={rows} summary={data?.summary ?? []} isLoading={isLoading}
      from={from} to={to} onFromChange={setFrom} onToChange={setTo}
      onExport={() => exportMutation.mutate()} exportPending={exportMutation.isPending}
      onRowClick={() => navigate('/achizitii/facturi')} />
  )
}

// ---------- Registru inventar (§12.2, obligatoriu legal, FR-INV-009/010) ----------

function RegistruInventarTab() {
  const navigate = useNavigate()
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const { data, isLoading } = useQuery({ queryKey: ['rapoarte', 'catalog', 'registru-inventar'], queryFn: listInventoryCounts })
  const exportMutation = useMutation({
    mutationFn: () => getInventoryCountsExportUrl({ from: from || undefined, to: to || undefined }),
    onSuccess: downloadBlob,
  })
  const filtered = (data ?? []).filter((c) => (!from || c.countDate >= from) && (!to || c.countDate <= to))

  const columns = useMemo<GridColDef<InventoryCountListItem>[]>(() => [
    { field: 'number', headerName: 'Număr', width: 150 },
    { field: 'countDate', headerName: 'Dată', width: 120 },
    { field: 'warehouseName', headerName: 'Depozit', flex: 1, minWidth: 180 },
    { field: 'status', headerName: 'Status', width: 120 },
  ], [])

  return (
    <Stack spacing={2}>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 2 }}>
        <TextField label="De la" type="date" size="small" value={from} onChange={(e) => setFrom(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
        <TextField label="Până la" type="date" size="small" value={to} onChange={(e) => setTo(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
      </Stack>
      <ExportButton onExport={() => exportMutation.mutate()} pending={exportMutation.isPending} />
      <Box sx={{ height: 480, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={filtered} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          onRowClick={() => navigate('/stocuri/inventar')}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>
    </Stack>
  )
}

// ---------- Comenzi client în curs (§12.2 #5) ----------

function ComenziInCursTab() {
  const navigate = useNavigate()
  const { data, isLoading } = useQuery({ queryKey: ['rapoarte', 'catalog', 'comenzi-in-curs'], queryFn: getOrdersInProgress })
  const exportMutation = useMutation({ mutationFn: getOrdersInProgressExportUrl, onSuccess: downloadBlob })

  const columns = useMemo<GridColDef<OrderInProgress>[]>(() => [
    { field: 'orderNumber', headerName: 'Comandă', width: 150 },
    { field: 'orderDate', headerName: 'Dată', width: 110 },
    { field: 'clientName', headerName: 'Client', flex: 1, minWidth: 180 },
    { field: 'status', headerName: 'Status', width: 150 },
    { field: 'deliveryTerm', headerName: 'Termen livrare', width: 130 },
    {
      field: 'isLate', headerName: 'Întârziată', width: 120,
      renderCell: (p) => <Chip size="small" label={p.row.isLate ? 'Da' : 'Nu'} color={p.row.isLate ? 'error' : 'success'} variant="outlined" />,
    },
    { field: 'total', headerName: 'Total', width: 120, type: 'number', valueFormatter: (v: number) => v.toFixed(2).replace('.', ',') },
    { field: 'currency', headerName: 'Monedă', width: 90 },
  ], [])

  return (
    <Stack spacing={2}>
      <ExportButton onExport={() => exportMutation.mutate()} pending={exportMutation.isPending} />
      <Box sx={{ height: 520, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          onRowClick={() => navigate('/comercial/comenzi')}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>
    </Stack>
  )
}

// ---------- Ordine de producție — deschise/finalizate/întârziate (§12.2 #10) ----------

const bucketColor: Record<ProductionOrderReportBucket, 'default' | 'error' | 'success'> = {
  Deschis: 'default', Intarziat: 'error', Finalizat: 'success',
}

function OrdineProductieRaportTab() {
  const navigate = useNavigate()
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const filters = { from: from || undefined, to: to || undefined }
  const { data, isLoading } = useQuery({
    queryKey: ['rapoarte', 'catalog', 'ordine-productie-raport', from, to],
    queryFn: () => getProductionOrdersStatusReport(filters),
  })
  const exportMutation = useMutation({ mutationFn: () => getProductionOrdersStatusReportExportUrl(filters), onSuccess: downloadBlob })

  const columns = useMemo<GridColDef<ProductionOrderStatusReportRow>[]>(() => [
    { field: 'number', headerName: 'OP', width: 140 },
    { field: 'productArticleCode', headerName: 'Cod', width: 110 },
    { field: 'productArticleDescription', headerName: 'Produs', flex: 1, minWidth: 180 },
    { field: 'plannedQuantityThousands', headerName: 'Cant. plan.', width: 110, type: 'number', valueFormatter: (v: number) => v.toFixed(3).replace('.', ',') },
    { field: 'plannedStartDate', headerName: 'Start', width: 110 },
    { field: 'plannedEndDate', headerName: 'Final', width: 110 },
    { field: 'equipmentName', headerName: 'Utilaj', width: 150 },
    { field: 'status', headerName: 'Status', width: 130 },
    {
      field: 'bucket', headerName: 'Stare', width: 120,
      renderCell: (p) => <Chip size="small" label={p.row.bucket} color={bucketColor[p.row.bucket]} variant="outlined" />,
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 2 }}>
        <TextField label="De la" type="date" size="small" value={from} onChange={(e) => setFrom(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
        <TextField label="Până la" type="date" size="small" value={to} onChange={(e) => setTo(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
      </Stack>
      <ExportButton onExport={() => exportMutation.mutate()} pending={exportMutation.isPending} />
      <Box sx={{ height: 520, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          onRowClick={() => navigate('/productie/ordine')}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>
    </Stack>
  )
}
